<template>
	<div class="site-settings">
		<div class="site-settings__container">
			<div class="logo-block">
				<div class="logo-block__container">
					<div class="logo-block__logo">
						<div
							v-if="!site.logo || isEditingLogo"
							class="logo-block__upload"
						>
							<LogoUpload
								:text="$t('loadFile')"
								@change="changeLogo"
							/>
						</div>
						<img
							v-else
							class="logo-block__img"
							:src="site.logo"
						>
					</div>
					<div class="logo-block__controls">
						<vs-button
							:disabled="!site.logo"
							@click="isEditingLogo = !isEditingLogo"
							class="logo-block__btn w-full pl-1 pr-1"
						>
							{{ isEditingLogo ? this.$t('cancel') : this.$t('changeLogo') }}
						</vs-button>

						<vs-custom-button
							:disabled="!site.logo"
							@click="site.logo = ''"
							type="border"
							color="danger"
							class="logo-block__btn w-full pl-1 pr-1"
						>{{ $t('deleteLogo') }}</vs-custom-button>
					</div>
				</div>
			</div>
			<div class="site-settings__inputs vx-row">
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.name"
						v-validate="'required|min:3'"
						name="name"
						:label="$t('name')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('name')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.uid"
						v-validate="'required|min:3'"
						name="uid"
						:label="`${$t('site')} ID`"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('uid')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.email"
						v-validate="'required|email'"
						name="email"
						label="E-Mail"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('email')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-password
						v-model="site.email_password"
						v-validate="'required|min:6'"
						name="password"
						type="password"
						:label="$t('newPassword')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('password')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.domain"
						v-validate="'required'"
						:label="$t('site')"
						class="site-settings__input w-full"
						name="site"
					/>
					<span v-if="errors.first('site')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-phone
						v-model="site.phone"
						v-validate="'required|min:7|max:14'"
						:label="$t('phone')"
						class="site-settings__input w-full"
						name="phone"
					/>
					<span v-if="errors.first('phone')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-input
						v-model="site.commission_manager_percent"
						v-validate="'required|numeric'"
						name="manager_percent"
						:label="$t('managersFee')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('manager_percent')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
          <vs-input
            v-model="site.marginality"
            v-validate="'required|numeric'"
            name="marginality"
						:label="$t('marginality')"
            class="site-settings__input w-full"
          />
          <span v-if="errors.first('marginality')" class="text-danger text-sm">
            {{ $t('fieldFilledIncorrect') }}
          </span>
        </div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.theme_color_main"
						name="main_color"
						v-model="site.theme_color_main"
						:label="$t('mainColor')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('main_color')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-color
						v-validate="'required'"
						:pickerColor="site.theme_color_secondary"
						name="additional_color"
						v-model="site.theme_color_secondary"
						:label="$t('additionalColor')"
						class="site-settings__input w-full"
					/>
					<span v-if="errors.first('additional_color')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-select
						v-validate="'required'"
						v-model="site.locale"
						:label="$t('locale')"
						class="site-settings__input w-full"
						name="main_color"
					>
						<vs-select-item value="de" :text="$t('german')"/>
						<vs-select-item value="fr" :text="$t('french')"/>
					</vs-select>
					<span v-if="errors.first('locale')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
				<div class="vx-col w-full sm:w-1/2">
					<vs-select
						v-validate="'required'"
						v-model="site.brand"
						:label="$t('siteBrand')"
						class="site-settings__input w-full"
						name="main_color"
					>
						<vs-select-item value="akad_eule" :text="$t('akadeule')"/>
						<vs-select-item value="wirschreiben" :text="$t('wirSchreiben')"/>
						<vs-select-item value="ghostwriter" :text="$t('ghostwriter')"/>
						<vs-select-item value="studienlab" :text="$t('studienLab')"/>
						<vs-select-item value="akadhilfe_24" :text="$t('akadHilfe24')"/>
						<vs-select-item value="other_brand" :text="$t('otherBrand')"/>
					</vs-select>
					<span v-if="errors.first('locale')" class="text-danger text-sm">
						{{ $t('fieldFilledIncorrect') }}
					</span>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import LogoUpload from "@/components/LogoUpload";
import databaseManager from "@/mixins/databaseManager";
import notifier from "@/mixins/notifier";

export default {
	name: "SiteSettings",
	mixins: [databaseManager, notifier],
	props: {
		site: { required: true, type: Object },
		validate: {required: true, type: Boolean }
	},
	components: {
		LogoUpload
	},
	data() {
		return {
			isEditingLogo: false
		};
	},
	watch: {
		validate() {
			this.makeValidate();
		}
	},
	methods: {
		makeValidate() {
			if(!this.validate) return;

			const entries = Object.entries(this.site);
			if(entries.length && this.validate) {
				this.$validator.validateAll();
			}
		},
		async changeLogo(path, files) {
			try {
				const data = [files[files.length - 1]];
				const res = await this.dbUploadMany(data);
				this.site.logo = res[0].fullUrl;
				this.isEditingLogo = false;

			} catch(e) {
				this.notifyError(e.message);
			}
		}
	},
	mounted() {
		this.makeValidate();
	}
};
</script>

<style lang="scss" scoped>
.site-settings {
	&__container {
		display: flex;
	}

	.logo-block {
		flex: 198px 0 0;

		&__container {
			width: 198px;
		}

		&__upload {
			width: 100%;
			height: 100%;
		}

		&__btn {
			margin-top: 15px
		}

		&__logo {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 200px;
			height: 87.5px;
			background-color: #eeeeee;
			border-radius: 10px;
			overflow: hidden;
		}

		&__img {
			max-width: 100%;
			max-height: 100%;
		}
	}

	&__inputs {
		margin-top: -17px;
		margin-left: 20px;
	}

	&__input {
		margin-top: 17px;
	}

	@media screen and (max-width: 992px) {
		&__container {
			flex-wrap: wrap;
		}

		.logo-block {
			flex: 100%;

			&__container {
				display: flex;
				width: 100%;
			}

			&__controls {
				flex-grow: 1;
				max-width: 180px;
				margin-left: 15px;
			}
		}

		&__inputs {
			margin-top: 20px;
			margin-left: -1rem;
		}
	}

	@media screen and (max-width: 576px) {
		.logo-block {
			&__container {
				flex-direction: column;
				align-items: center;
			}

			&__controls {
				max-width: 198px;
				margin-left: 0;
			}
		}
	}
}
</style>
